import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { SERVICE_OPTIONS } from "../lib/site-data";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  services: [],
  message: "",
};

export const ConsultationForm = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s)
        ? f.services.filter((x) => x !== s)
        : [...f.services, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setDone(true);
      setForm(initial);
      toast.success("Thanks! We'll reach out within 1 business day.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong submitting the form. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="consultation"
      data-testid="consultation-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8">
        <div className="grid grid-cols-12 gap-y-10 lg:gap-10 items-start">
          <div className="col-span-12 lg:col-span-5">
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-green-100 text-[color:var(--brand-green-700)] px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase">
              Let's Talk
            </span>
            <h2 className="font-display mt-5 text-4xl md:text-5xl font-bold text-navy">
              Book a free consultation.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Tell us what you need help with — RCM, credentialing, licensing, or bookkeeping. A
              senior consultant will get back to you within one business day.
            </p>

            <div className="mt-8 space-y-4 text-[15px]">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-brand-green-100 text-[color:var(--brand-green-700)] flex items-center justify-center font-bold">
                  @
                </span>
                <a
                  href="mailto:info@medielitesolutions.com"
                  className="text-slate-800 hover:text-navy"
                  data-testid="contact-email"
                >
                  info@medielitesolutions.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-brand-green-100 text-[color:var(--brand-green-700)] flex items-center justify-center font-bold">
                  ☎
                </span>
                <a
                  href="tel:+17868915066"
                  className="text-slate-800 hover:text-navy"
                  data-testid="contact-phone"
                >
                  +1 786-891-5066
                </a>
              </div>
              <div className="flex items-start gap-3" data-testid="contact-address">
                <span className="w-10 h-10 rounded-lg bg-brand-green-100 text-[color:var(--brand-green-700)] flex items-center justify-center font-bold shrink-0">
                  ⌖
                </span>
                <div className="text-slate-800 leading-relaxed">
                  <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--brand-green-700)] font-bold">
                    Main Business Address
                  </div>
                  <div className="mt-0.5">971 US Highway 202 N,</div>
                  <div>Branchburg, New Jersey 08876</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            {done ? (
              <div
                data-testid="consultation-success"
                className="bg-[color:var(--brand-green-50)] border border-[color:var(--brand-green-200)] rounded-2xl p-10 text-center"
              >
                <div className="w-14 h-14 rounded-full bg-[color:var(--brand-green-300)] text-navy mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display text-2xl text-navy mt-5">
                  Thanks — your request has been received.
                </h3>
                <p className="text-slate-600 mt-2">
                  A MediElite specialist will reach out shortly.
                </p>
                <Button
                  onClick={() => setDone(false)}
                  data-testid="consultation-submit-another"
                  className="mt-6 rounded-full bg-navy text-white hover:bg-[color:var(--brand-navy-dark)]"
                >
                  Submit another request
                </Button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="consultation-form"
                className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-sm text-navy font-semibold">
                      Full name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="form-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Dr. Jane Doe"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-sm text-navy font-semibold">
                      Practice / Company
                    </Label>
                    <Input
                      id="company"
                      data-testid="form-company"
                      value={form.company}
                      onChange={(e) => update("company", e.target.value)}
                      placeholder="Your practice name"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm text-navy font-semibold">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="form-email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm text-navy font-semibold">
                      Phone *
                    </Label>
                    <Input
                      id="phone"
                      data-testid="form-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="+1 ___ ___ ____"
                      className="mt-1.5"
                      required
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <Label className="text-sm text-navy font-semibold">
                    Services you need help with
                  </Label>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {SERVICE_OPTIONS.map((s) => {
                      const id = `svc-${s.replace(/\W+/g, "-").toLowerCase()}`;
                      const checked = form.services.includes(s);
                      return (
                        <label
                          key={s}
                          htmlFor={id}
                          data-testid={`form-service-${id}`}
                          className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all ${
                            checked
                              ? "border-[color:var(--brand-green-400)] bg-[color:var(--brand-green-50)]"
                              : "border-slate-200 hover:border-[color:var(--brand-green-300)]"
                          }`}
                        >
                          <Checkbox
                            id={id}
                            checked={checked}
                            onCheckedChange={() => toggleService(s)}
                          />
                          <span className="text-sm text-slate-800">{s}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6">
                  <Label htmlFor="message" className="text-sm text-navy font-semibold">
                    Tell us briefly what you need
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="form-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="A few sentences about your practice, specialty, or the challenge you're facing."
                    rows={4}
                    className="mt-1.5"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="consultation-form-submit"
                  className="mt-7 w-full md:w-auto rounded-full bg-navy text-white hover:bg-[color:var(--brand-navy-dark)] px-8 py-6 text-base"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Send my request <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                <p className="mt-3 text-xs text-slate-500 leading-relaxed" data-testid="form-sms-consent">
                  Welcome to MediElite Practice Solutions. By providing your telephone number and
                  submitting this form, you consent to receive SMS text messages from MediElite
                  Practice Solutions regarding account-related communications, service updates,
                  notifications. Message and data rates may apply. You may opt out at any time by
                  replying STOP, and you may reply HELP for assistance. See our{" "}
                  <a
                    href="/privacy"
                    className="underline underline-offset-2 hover:text-navy"
                    data-testid="form-privacy-link"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
